<template>
    <div class="continue-outer-container" :class="{ 'dark-mode': darkMode }">
        <Toast />
        <!-- Loading overlay for dark mode -->
        <div v-if="darkMode && isAuthLoading" class="dark-loading-overlay">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ t('general.loading') }}</p>
            <div class="loading-dots" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>

        <div v-if="showEmailVerification" class="verification-modal-container">
            <EmailVerificationFlow
                mode="modal"
                :dark-mode="darkMode"
                @verified="handleVerificationSuccess"
            />
        </div>

        <div v-else class="main-auth-content">
            <!-- Logo -->
            <h1 class="welcome-title">
                {{
                    displayName
                        ? `${$t('login.welcome')} ${displayName}`
                        : $t('login.welcome')
                }}
            </h1>
            <p class="continue-description">
                {{ $t('login.welcome-description-1') }}
                <strong>{{ $t('login.welcome-description-2') }}</strong>
                {{ $t('login.welcome-description-3') }}
                <strong
                    ><router-link
                        to="/legal/terms-of-use"
                        target="_blank"
                        class="link_terms"
                        >{{ $t('login.welcome-description-4') }}</router-link
                    ></strong
                >
                {{ $t('login.welcome-description-5') }}
                <strong
                    ><router-link
                        to="/legal/privacy-policy"
                        target="_blank"
                        class="link_terms"
                        >{{ $t('login.welcome-description-6') }}</router-link
                    ></strong
                >
            </p>

            <!-- Continue with Google -->
            <Button
                class="shadow-btn google-button"
                :label="`${$t('login.continue-with')} Google`"
                @click="signInWithGoogle"
                :disabled="loadingOAuth"
            >
                <!-- Google icon png -->
                <template #icon>
                    <img
                        src="@/assets/images/continue/Google.svg"
                        alt="Google"
                        class="button-icon"
                    />
                </template>
            </Button>

            <!-- Continue with X -->
            <!-- Hidden but logic preserved -->
            <Button
                v-if="false"
                class="shadow-btn x-button"
                :label="`${$t('login.continue-with')} X`"
                @click="signInWithX"
                :disabled="loadingOAuth"
            >
                <!-- X icon png -->
                <template #icon>
                    <img
                        src="@/assets/images/continue/x.svg"
                        alt="X"
                        class="button-icon"
                    />
                </template>
            </Button>

            <!-- Divider with 'or' text -->
            <div class="divider">
                <div class="divider-line"></div>
                <span class="divider-text">{{ $t('general.or') }}</span>
                <div class="divider-line"></div>
            </div>

            <!-- Email input field -->
            <div class="email-input-container">
                <div class="input-wrapper">
                    <InputText
                        ref="emailInputRef"
                        placeholder="email@example.com"
                        class="input-field"
                        v-model="email"
                        @keyup.enter="continueWithEmail"
                        :class="{ 'checking-email': isCheckingEmail }"
                    />
                    <div v-if="isCheckingEmail" class="input-spinner">
                        <i class="pi pi-spin pi-spinner"></i>
                    </div>
                </div>
            </div>

            <!-- Password field (shown only after email is validated) -->
            <transition name="slide-fade">
                <div v-if="showPassword" class="email-input-container">
                    <Password
                        id="password"
                        unstyled
                        placeholder="password"
                        v-model="password"
                        :feedback="false"
                        @keyup.enter="handleEmailLogin"
                    />
                </div>
            </transition>

            <!-- Continue with email button -->
            <Button
                class="shadow-btn email-button"
                @click="showPassword ? handleEmailLogin() : continueWithEmail()"
                :loading="isLoadingEmailAuth"
                :disabled="isLoadingEmailAuth"
            >
                <img
                    src="@/assets/images/continue/icon-candado.svg"
                    alt="Email"
                    class="button-icon email-button-icon"
                />
                <!-- Button label with transition -->
                <transition name="button-text-fade" mode="out-in">
                    <span
                        class="button-text"
                        :key="
                            isEmailRegistered === undefined
                                ? 'continue'
                                : isEmailRegistered === false
                                  ? 'signup'
                                  : 'login'
                        "
                    >
                        {{
                            isEmailRegistered === undefined
                                ? `${$t('login.continue-with')} Email`
                                : isEmailRegistered === false
                                  ? `${$t('login.signup')}`
                                  : `${$t('general.login')}`
                        }}
                    </span>
                </transition>
            </Button>

            <!-- Forgot password link (only shown when password field is visible) -->
            <transition name="slide-fade">
                <div
                    v-if="showPassword && isEmailRegistered"
                    class="forgot-password-link"
                >
                    <a
                        @click="resetPassword"
                        class="text-sm forgot-password-link-text"
                    >
                        {{ $t('login.forgot-password') }}
                    </a>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
// Vue core imports
import { ref, watch, onMounted, nextTick, onActivated, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// Store imports
import { useAuthStore } from '@/stores/auth.store'
import { useModalStore } from '@/stores/modal.store'
import { usePaymentStore } from '@/stores/payment.store'

// Service imports
import UserService from '@/services/api/user.service.js'

// Utilities
import { debounce } from '@/utils'
import { clearTargetCreator } from '@/utils/clientIntent'
import * as authUtils from '@/utils/auth.js'
import { linkAnonymousAccount } from '@/utils/auth.js'
import * as toastUtils from '@/utils/toast.js'
import { safeLocalStorageRemove, safeLocalStorageSet } from '@/utils/storage.js'
import { upgradeUserToClient } from '@/utils/accountUpgrade.js'
import { useGuardedActions } from '@/composables/useGuardedActions'

// Date/Time utilities
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// UI Components
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import EmailVerificationFlow from '@/components/authentication/EmailVerificationFlow.vue'

// Import firebase
import firebase from 'firebase/compat/app'

// Setup
dayjs.extend(utc)
dayjs.extend(timezone)

const router = useRouter()
const toast = useToast()
const { t } = useI18n()
const authStore = useAuthStore()
const modalStore = useModalStore()
const paymentStore = usePaymentStore()
const { resumeAction } = useGuardedActions()

const props = defineProps({
    isPage: {
        type: Boolean,
        default: false,
    },
    hideTitle: {
        type: Boolean,
        default: false,
    },
    darkMode: {
        type: Boolean,
        default: false,
    },
    displayName: {
        type: String,
        default: '',
    },
    disableRedirect: {
        type: Boolean,
        default: false,
    },
    forceClientOnboarding: {
        type: Boolean,
        default: false,
    },
    onboardingRedirectTo: {
        type: String,
        default: '',
    },
    keepOpenOnAuth: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['auth-loading'])

// Form state
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isEmailRegistered = ref(undefined)
const emailPreviouslyChecked = ref(false)
const isCheckingEmail = ref(false)
const loadingOAuth = ref(false)
const isLoadingEmailAuth = ref(false)
const showEmailVerification = ref(false)
const isRedirecting = ref(false)

// Refs
const emailInputRef = ref(null)

const isAuthLoading = computed(
    () =>
        isLoadingEmailAuth.value ||
        loadingOAuth.value ||
        isRedirecting.value
)

watch(
    isAuthLoading,
    (value) => {
        emit('auth-loading', value)
    },
    { immediate: true }
)

// Ensure email is always lowercase
watch(
    email,
    (newVal) => {
        if (newVal && newVal !== newVal.toLowerCase()) {
            email.value = newVal.toLowerCase()
        }
    },
    { immediate: true }
)

// Create a debounced version of the email check function
const debouncedCheckEmail = debounce(async (newEmail) => {
    if (authUtils.validEmail(newEmail)) {
        isCheckingEmail.value = true
        const uniqueEmail = await authUtils.isUniqueEmail(newEmail)
        isEmailRegistered.value = !uniqueEmail
        isCheckingEmail.value = false
    }
}, 500) // 500ms delay

// Watch for changes to email after initial check
watch(email, (newEmail, oldEmail) => {
    if (
        emailPreviouslyChecked.value &&
        showPassword.value &&
        newEmail !== oldEmail
    ) {
        // Use the debounced version to limit API calls
        debouncedCheckEmail(newEmail)
    }
})

function validEmail(email) {
    return authUtils.validEmail(email)
}

async function isUniqueEmail(email) {
    return await authUtils.isUniqueEmail(email)
}

function resetAuthForm() {
    email.value = ''
    password.value = ''
    showPassword.value = false
    isEmailRegistered.value = undefined
    emailPreviouslyChecked.value = false
    isCheckingEmail.value = false
}

async function handlePendingAction() {
    const pendingAction = localStorage.getItem('pendingAction')
    if (pendingAction === 'addFunds') {
        localStorage.removeItem('pendingAction')
        // We don't remove pendingPackId here; add-funds modal can use it if needed.
        modalStore.closeModal()
        paymentStore.state.showBuyStarsModal = true
        return
    }
}

const buildClientOnboardingRoute = () => {
    if (props.onboardingRedirectTo) {
        return {
            name: 'client-profile-setup',
            query: { postOnboarding: props.onboardingRedirectTo },
        }
    }
    return 'client-profile-setup'
}

const ensureClientUpgradeForForcedFlow = async (userData) => {
    if (!props.forceClientOnboarding) return userData
    if (!userData) return userData

    if (userData?.isSystemUser) {
        return userData
    }

    const userType = userData?.typeofuser
    if (userType === 'Client' || userType === 'Creator' || userType === 'Admin') {
        return userData
    }

    try {
        const result = await upgradeUserToClient({ authStore })
        if (result?.success) {
            return authStore.user?.data || userData
        }
    } catch (error) {
        console.error('Forced client upgrade failed:', error)
    }

    return userData
}

// Centralized navigation logic function
async function getRedirectRoute(userData, isNewUser) {
    if (userData?.isSystemUser) {
        return 'client-chats'
    }
    const actualUserType = userData?.typeofuser
    const finishedSetup = userData?.finishedSetup
    const shouldForceClientOnboarding =
        props.forceClientOnboarding &&
        actualUserType !== 'Creator' &&
        actualUserType !== 'Admin'

    if (!actualUserType) {
        if (props.forceClientOnboarding) {
            console.warn(
                'User type missing; defaulting to client onboarding for forced flow.',
                userData
            )
            return buildClientOnboardingRoute()
        }
        console.error(
            'User type not found in backend response for redirection logic!',
            userData
        )
        return null // Signal to caller to handle error (e.g., show toast, redirect to home)
    }

    // For new users OR users who haven't finished setup
    if (isNewUser || !finishedSetup) {
        if (shouldForceClientOnboarding) {
            return buildClientOnboardingRoute()
        }

        // Clients go directly to chats - setup happens lazily via conversation.store gate
        // when they actually try to start a chat
        if (actualUserType === 'Client') {
            return 'client-chats'
        }

        return 'account-creation'
    }

    // For existing users who have finished setup
    if (actualUserType === 'Creator') {
        // Since finishedSetup means KYC is good, just go to creator-chats
        return 'creator-chats'
    } else if (actualUserType === 'Client') {
        return 'client-chats'
    }

    if (actualUserType === 'Admin') {
        return 'admin-decaf-payouts'
    }

    console.error(
        'Unhandled user type or state for redirection (existing, finishedSetup):',
        userData
    )
    return null // Fallback
}

const isAccountCreationTarget = (target) => {
    if (!target) return false
    if (typeof target === 'string') {
        return target === 'account-creation'
    }
    return target.name === 'account-creation' || target.path === '/account-creation'
}

const pushRedirect = async (target) => {
    if (!target) return
    if (props.forceClientOnboarding) {
        if (isAccountCreationTarget(target)) {
            safeLocalStorageSet('forceClientSignup', 'client')
        } else {
            safeLocalStorageRemove('forceClientSignup')
        }
    }
    if (typeof target === 'string') {
        await router.push({ name: target })
        if (props.keepOpenOnAuth) {
            modalStore.closeModal()
        }
        return
    }
    await router.push(target)
    if (props.keepOpenOnAuth) {
        modalStore.closeModal()
    }
}

async function continueWithEmail() {
    // Validate email
    if (!validEmail(email.value)) {
        toastUtils.showWarning(toast, t('login.invalid-email'))
        return
    }

    // Check if the email is already registered
    try {
        isCheckingEmail.value = true
        const uniqueEmail = await isUniqueEmail(email.value)
        isEmailRegistered.value = !uniqueEmail

        // Show password field and focus it after a brief delay to allow animation
        showPassword.value = true
        emailPreviouslyChecked.value = true

        // Focus password field after animation completes
        setTimeout(() => {
            const passwordInput = document.querySelector('#password input')
            if (passwordInput) {
                passwordInput.focus()
            }
        }, 300) // Match this with your slide-fade transition duration

        isCheckingEmail.value = false
    } catch (error) {
        isCheckingEmail.value = false
        console.error('Error checking email:', error)
        toastUtils.showError(toast, t('general.general-error'))
    }
}

async function handleEmailLogin() {
    if (!password.value) {
        toastUtils.showWarning(toast, t('login.enter-password'))
        return
    }

    // Password length check
    if (password.value.length < 6) {
        toastUtils.showWarning(
            toast,
            t('login.password-min-length', { min: 6 })
        )
        return
    }

    if (isEmailRegistered.value) {
        // Login with existing account
        await loginWithEmail()
    } else {
        // Create new account
        await signupWithEmail()
    }
}

async function loginWithEmail() {
    try {
        isLoadingEmailAuth.value = true
        const result = await authUtils.loginWithEmail(
            email.value,
            password.value
        )

        // NOTE: Email verification for creators is temporarily disabled
        // Uncomment the block below to re-enable
        // if (result.success && result.requiresEmailVerification) {
        //     isLoadingEmailAuth.value = false
        //     showEmailVerification.value = true
        //     return
        // }

        if (result.success && result.user) {
            // Manually update the auth store to make it available for subsequent actions
            const currentFirebaseUser = firebase.auth().currentUser
            if (currentFirebaseUser) {
                authStore.setUser(currentFirebaseUser)
            }
            authStore.user.data = result.user
            console.log(
                'Auth store updated directly after email login:',
                authStore.user.data
            )

            // Check if redirect is disabled
            if (props.disableRedirect) {
                toastUtils.showSuccess(
                    toast,
                    t('general.welcome'),
                    t('login.success')
                )
                // Don't redirect, just return - let the parent component handle what happens next
                return
            }

            isRedirecting.value = true
            const resumed = await resumeAction()
            if (resumed) {
                toastUtils.showSuccess(
                    toast,
                    t('general.welcome'),
                    t('login.success')
                )
                return
            }

            const resolvedUser = await ensureClientUpgradeForForcedFlow(
                result.user
            )
            authStore.user.data = resolvedUser
            const redirectTarget = await getRedirectRoute(resolvedUser, false) // false for isNewUser

            if (redirectTarget) {
                toastUtils.showSuccess(
                    toast,
                    t('general.welcome'),
                    t('login.success')
                )
                await waitForAppInitialization()
                await pushRedirect(redirectTarget)
            } else {
                isRedirecting.value = false
                const unsupportedRoles = ['Partner', 'Manager', 'SuperstudioManager']
                if (unsupportedRoles.includes(resolvedUser?.typeofuser)) {
                    toastUtils.showError(toast, t('general.error'), t('login.unsupported-role'))
                    await firebase.auth().signOut()
                    authStore.clearUser()
                    resetAuthForm()
                } else {
                    toastUtils.showError(toast, t('general.general-error'))
                }
            }
        } else {
            toastUtils.showWarning(toast, t('login.check-email-and-password'))
        }
    } catch (error) {
        isRedirecting.value = false
        console.error('Login error:', error)
        toastUtils.showWarning(toast, t('login.check-email-and-password'))
    } finally {
        isLoadingEmailAuth.value = false
    }
}

async function signupWithEmail() {
    try {
        isLoadingEmailAuth.value = true

        // Check if we are upgrading an anonymous user
        const isAnonymous = firebase.auth().currentUser?.isAnonymous

        if (isAnonymous) {
            const credential = firebase.auth.EmailAuthProvider.credential(
                email.value,
                password.value
            )
            const linkResult = await linkAnonymousAccount(credential)

            if (linkResult.success) {
                // Update backend to remove isAnonymous flag
                await UserService.updateUser({
                    isAnonymous: false,
                    email: email.value,
                })

                // Fetch fresh user data
                await authStore.updateUserData(true)

                toastUtils.showSuccess(
                    toast,
                    t('general.welcome'),
                    t('login.success')
                )

                // Handle pending actions. This flow upgrades an anonymous user (first registration).
                await handlePendingAction()

                modalStore.closeModal()
                isLoadingEmailAuth.value = false
                return
            } else {
                // Handle link error (e.g. email already in use)
                let errorMessage = linkResult.error
                if (linkResult.code === 'auth/email-already-in-use') {
                    errorMessage = t('login.invalid-email') // Or a more specific message
                }
                toastUtils.showError(toast, errorMessage)
                isLoadingEmailAuth.value = false
                return
            }
        }

        isLoadingEmailAuth.value = true
        // Pass redirect URL for verification email
        const redirectUrl = window.location.origin + '/email-verification'
        const result = await authUtils.signupWithEmail(
            email.value,
            password.value,
            redirectUrl
        )

        if (result.success && result.data) {
            const currentFirebaseUser = firebase.auth().currentUser
            if (currentFirebaseUser) {
                authStore.setUser(currentFirebaseUser)
            }
            authStore.user.data = result.data
            console.log(
                'Auth store updated directly after signup:',
                authStore.user.data
            )

            // NOTE: Email verification for creators is temporarily disabled
            // All users now proceed with normal flow (account-creation first)
            // Uncomment the block below to re-enable email verification for Creators
            // if (result.data?.typeofuser === 'Creator') {
            //     showEmailVerification.value = true
            // } else {
            // For all users, proceed with normal flow
            // Check if redirect is disabled
            if (props.disableRedirect) {
                toastUtils.showSuccess(
                    toast,
                    t('login.user-registered'),
                    t('login.user-registered-detail')
                )
                // Handle pending actions before closing
                // This is a new registration (not anonymous upgrade, but still new), so pass true
                // to be consistent with OAuth signup logic which uses result.isNew
                await handlePendingAction()
                // Close the modal since we're staying on the same route
                modalStore.closeModal()
                return
            }

            isRedirecting.value = true
            const resumed = await resumeAction()
            if (resumed) {
                toastUtils.showSuccess(
                    toast,
                    t('login.user-registered'),
                    t('login.user-registered-detail')
                )
                return
            }

            const resolvedUser = await ensureClientUpgradeForForcedFlow(
                result.data
            )
            authStore.user.data = resolvedUser
            const redirectTarget = await getRedirectRoute(resolvedUser, true) // true for isNewUser

            if (redirectTarget) {
                toastUtils.showSuccess(
                    toast,
                    t('login.user-registered'),
                    t('login.user-registered-detail')
                )
                await waitForAppInitialization()
                await pushRedirect(redirectTarget)
            } else {
                isRedirecting.value = false
                const unsupportedRoles = ['Partner', 'Manager', 'SuperstudioManager']
                if (unsupportedRoles.includes(resolvedUser?.typeofuser)) {
                    toastUtils.showError(toast, t('general.error'), t('login.unsupported-role'))
                    await firebase.auth().signOut()
                    authStore.clearUser()
                    resetAuthForm()
                } else {
                    toastUtils.showError(toast, t('general.general-error'))
                }
            }
            // }
        } else {
            // Handle signup failure reported by authUtils.signupWithEmail
            console.error('Signup failed:', result.error)
            toastUtils.showError(
                toast,
                result.error || t('general.general-error'),
                null
            )
        }
    } catch (error) {
        isRedirecting.value = false
        // Catch unexpected errors during the signup process itself
        console.error('Unexpected error during signup:', error)
        toastUtils.showError(
            toast,
            t('general.general-error'),
            error.message || t('login.signup-error-detail')
        )
    } finally {
        isLoadingEmailAuth.value = false
    }
}

async function resetPassword() {
    // Clear any potential leftover redirect targets on password reset attempt
    clearTargetCreator()

    if (!email.value) {
        toastUtils.showWarning(toast, t('login.enter-email'))
        return
    }

    const success = await authUtils.sendPasswordResetEmail(email.value)
    if (success) {
        toastUtils.showInfo(toast, t('login.check-email'))
    } else {
        toastUtils.showWarning(toast, t('general.general-error'))
    }
}

// --- Function to handle the result from signInWithProvider ---
async function handleAuthResult(result) {
    if (result.success && result.data) {
        // 1. Set basic Firebase user info (optional but good practice)
        const currentFirebaseUser = firebase.auth().currentUser
        if (currentFirebaseUser) {
            authStore.setUser(currentFirebaseUser)
        } else {
            console.warn(
                'OAuth successful but Firebase currentUser is null. Cannot set basic user info.'
            )
        }

        // 2. Set the detailed backend user data DIRECTLY
        authStore.user.data = result.data
        console.log(
            'Auth store updated directly with backend data:',
            authStore.user.data
        )

        // 3. Navigate to the appropriate page (unless redirect is disabled)
        if (props.disableRedirect) {
            toastUtils.showSuccess(
                toast,
                result.isNew
                    ? t('login.user-registered')
                    : t('login.login-successful'),
                null // Using a generic success detail
            )
            // Handle pending actions before closing
            await handlePendingAction()
            // Close the modal since we're staying on the same route
            modalStore.closeModal()
            return
        }

        isRedirecting.value = true
        let postLoginRedirected = false
        if (!result.isNew) {
            // Only check for stored intent if it was a login (existing user)
            postLoginRedirected = await resumeAction()
        }
        if (postLoginRedirected) {
            toastUtils.showSuccess(
                toast,
                result.isNew
                    ? t('login.user-registered')
                    : t('login.login-successful'),
                null // Using a generic success detail
            )
            return
        }

        const resolvedUser = await ensureClientUpgradeForForcedFlow(result.data)
        authStore.user.data = resolvedUser
        const redirectTarget = await getRedirectRoute(
            resolvedUser,
            result.isNew
        )

        if (redirectTarget) {
            toastUtils.showSuccess(
                toast,
                result.isNew
                    ? t('login.user-registered')
                    : t('login.login-successful'),
                null // Using a generic success detail
            )
            // Prevent stale referral code for non-referral logins (existing users bypassing account-creation)
            if (!result.isNew) {
                localStorage.removeItem('loginTargetCreatorRefCode')
                await waitForAppInitialization()
            }
            await pushRedirect(redirectTarget)
        } else {
            isRedirecting.value = false
            const unsupportedRoles = ['Partner', 'Manager', 'SuperstudioManager']
            if (unsupportedRoles.includes(resolvedUser?.typeofuser)) {
                toastUtils.showError(toast, t('general.error'), t('login.unsupported-role'))
                await firebase.auth().signOut()
                authStore.clearUser()
                resetAuthForm()
            } else {
                console.error(
                    'OAuth successful but could not determine redirect route from backend data:',
                    result.data
                )
                toastUtils.showError(toast, t('general.general-error'))
            }
        }
    } else if (result.userCancelled) {
        isRedirecting.value = false
        // User closed the popup - often no notification is needed
        console.log('Sign-in cancelled by user.')
    } else {
        isRedirecting.value = false
        // Handle other errors reported by signInWithProvider
        console.error('OAuth Error:', result.error, 'Code:', result.code)
        // Clear potential target if OAuth fails
        // Clear potential target if OAuth fails
        clearTargetCreator()
        toastUtils.showError(
            toast,
            result.error || t('general.general-error') // Show specific error or fallback
        )
    }
}

const handleOAuthSignIn = async (providerFn) => {
    // Prevent multiple attempts
    if (loadingOAuth.value) {
        console.log('OAuth sign-in already in progress.')
        return
    }

    loadingOAuth.value = true

    try {
        const isAnonymous = firebase.auth().currentUser?.isAnonymous

        if (isAnonymous) {
            const provider = providerFn()
            const result = await firebase
                .auth()
                .currentUser.linkWithPopup(provider)

            // Update backend to remove isAnonymous flag
            await UserService.updateUser({
                isAnonymous: false,
                email: result.user.email,
            })

            // Fetch fresh user data
            await authStore.updateUserData(true)

            toastUtils.showSuccess(
                toast,
                t('general.welcome'),
                t('login.success')
            )

            // This flow upgrades an anonymous user (first registration).
            await handlePendingAction()
            modalStore.closeModal()
        } else {
            const result = await authUtils.signInWithProvider(providerFn)
            // Process the result using the handler function
            await handleAuthResult(result)
        }
    } catch (error) {
        // Catch any unexpected errors
        console.error(
            'Unexpected error during OAuth sign-in initiation:',
            error
        )
        toastUtils.showError(toast, t('general.general-error'))
    } finally {
        // Reset loading state regardless of outcome
        loadingOAuth.value = false
    }
}

// Use the updated handleOAuthSignIn
const signInWithGoogle = () => handleOAuthSignIn(authUtils.googleProvider)
const signInWithX = () => handleOAuthSignIn(authUtils.xProvider)

async function waitForAppInitialization() {
    const maxWaitTime = 15000 // Increased to 15 seconds for cross-tab email verification flows
    const startTime = Date.now()

    while (
        !authStore.state.appInitialized &&
        Date.now() - startTime < maxWaitTime
    ) {
        await new Promise((resolve) => setTimeout(resolve, 100))
    }

    if (!authStore.state.appInitialized) {
        console.warn(
            'App initialization did not complete in time, proceeding anyway'
        )
        console.log('Current auth state:', {
            hasUserId: !!authStore.user?.data?._id,
            hasUid: !!authStore.user?.uid,
            appInitialized: authStore.state.appInitialized,
        })
        console.log(
            'Note: This is usually fine - the app will continue initializing in the background'
        )
    } else {
        console.log('App initialization completed, user data ready')
    }
}

async function handleVerificationSuccess() {
    try {
        const user = await UserService.getCurrentUser()

        if (user && user.status === 200 && user.data) {
            authStore.user.data = user.data

            isRedirecting.value = true
            const resumed = await resumeAction()
            if (resumed) {
                modalStore.closeModal()
                resetAuthForm()
                return
            }

            const resolvedUser = await ensureClientUpgradeForForcedFlow(user.data)
            authStore.user.data = resolvedUser
            const redirectTarget = await getRedirectRoute(
                resolvedUser,
                !resolvedUser?.finishedSetup
            )

            if (redirectTarget) {
                await waitForAppInitialization()
                modalStore.closeModal()
                await pushRedirect(redirectTarget)
            } else {
                isRedirecting.value = false
                const unsupportedRoles = ['Partner', 'Manager', 'SuperstudioManager']
                if (unsupportedRoles.includes(resolvedUser?.typeofuser)) {
                    toastUtils.showError(toast, t('general.error'), t('login.unsupported-role'))
                    await firebase.auth().signOut()
                    authStore.clearUser()
                    resetAuthForm()
                } else {
                    toastUtils.showError(toast, t('general.general-error'))
                }
            }
        } else {
            isRedirecting.value = false
            toastUtils.showError(toast, t('general.general-error'))
        }
    } catch (error) {
        isRedirecting.value = false
        console.error('Error handling verified redirect:', error)
        toastUtils.showError(toast, t('general.general-error'))
    } finally {
        showEmailVerification.value = false
        isLoadingEmailAuth.value = false
        resetAuthForm()
    }
}

// Function to focus the email input field
async function focusEmailInput() {
    await nextTick()
    if (emailInputRef.value && !showEmailVerification.value) {
        try {
            // PrimeVue InputText component - access the input element
            const component = emailInputRef.value
            let inputElement = null

            // PrimeVue InputText exposes the input via $el or we can query it
            if (component.$el) {
                // $el might be the input itself or a wrapper
                inputElement =
                    component.$el.tagName === 'INPUT'
                        ? component.$el
                        : component.$el.querySelector('input')
            }

            // Fallback: query by class or placeholder
            if (!inputElement) {
                inputElement =
                    document.querySelector('.input-field input') ||
                    document.querySelector(
                        'input[placeholder="email@example.com"]'
                    )
            }

            if (inputElement && typeof inputElement.focus === 'function') {
                // Small delay to ensure modal animation completes
                setTimeout(() => {
                    inputElement.focus()
                }, 100)
            }
        } catch (error) {
            console.warn('Could not focus email input:', error)
        }
    }
}

// Focus email input when component mounts (modal opens)
onMounted(() => {
    focusEmailInput()
})

// Focus email input when component is activated (if using keep-alive)
onActivated(() => {
    focusEmailInput()
})

// Watch for when email verification modal closes to refocus email input
watch(showEmailVerification, (newVal) => {
    if (!newVal) {
        // Email verification modal closed, refocus email input
        focusEmailInput()
    }
})
</script>

<style scoped>
.continue-outer-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.verification-modal-container {
    width: 100%;
    max-width: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
}

.main-auth-content {
    width: 250px;
    max-width: 250px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
}

.welcome-title {
    font-size: 30px;
    font-weight: 100;
    margin-bottom: 20px;
    line-height: 1.3;
    text-align: center;
    font-family: 'Basier Circle', sans-serif;
    text-wrap: balance;
    color: #393939;
}

.continue-description {
    font-size: 14px;
    color: #393939;
    text-align: center;
    margin-bottom: 40px;
    line-height: 1.4;
    font-family: 'Nunito', sans-serif;
    width: 250px;
}

.shadow-btn {
    margin-bottom: 12px;
    width: 100%;
    height: 50px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 5px #00000021;
    font-size: 14px;
    font-weight: 300;
    color: rgb(23, 23, 23);
}

.google-button {
    background-color: #fff;
    color: #000;
    border: none;
}

.x-button {
    background-color: #000;
    color: #fff;
    border: none;
}

.email-button {
    background-color: #f0f0f0;
    color: #333;
    border: none;
}

.button-icon {
    margin-right: 10px;
    width: 20px;
    height: 20px;
}

.link_terms {
    color: #393939;
    text-decoration: underline;
}

/* Divider styles */
.divider {
    display: flex;
    align-items: center;
    width: 100%;
    margin: 15px 0;
    gap: 15px;
}

.divider-line {
    flex: 1;
    height: 1px;
    background-color: #ddd;
}

.divider-text {
    color: #999;
    font-size: 14px;
    white-space: nowrap;
}

/* Email input styles */
.email-input-container {
    width: 100%;
    margin-bottom: 12px;
}

.input-wrapper {
    position: relative;
}

/* input {
  width: 256px !important;
  height: 24px !important;
} */

.input-field {
    width: 100%;
    height: 44px;
    border-radius: 8px !important;
    padding: 10px 12px !important;
    font-size: 14px !important;
    border: 1px solid #ddd !important;
    color: #333 !important;
}

input[data-pc-name='inputtext'] {
    border: 1px solid #ddd !important;
    color: #333 !important;
    z-index: 1;
    /* padding-right: 35px !important; */
}

input[data-pc-name='inputtext']::placeholder {
    color: #999 !important;
}

/* Forgot password link */
.forgot-password-link {
    width: 100%;
    text-align: right;
    margin-bottom: 15px;
}

.forgot-password-link a {
    cursor: pointer;
    color: black;
    font-size: 12px !important;
    text-decoration: underline;
    cursor: pointer;
}

/* Slide-fade transition animation for password field */
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(-20px);
    opacity: 0;
}

/* Email checking indicator styles */
.input-spinner {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #9B6E2B;
    z-index: 2;
}

.checking-email {
    border-color: #9B6E2B !important;
}

/* Button text transition */
.button-text-fade-enter-active,
.button-text-fade-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.button-text-fade-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.button-text-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.button-text {
    width: 218px;
}

/* Optional: Style for disabled buttons */
.shadow-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Dark mode loading overlay */
.dark-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    gap: 16px;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    color: white;
    font-size: 14px;
    font-family: 'Nunito', sans-serif;
    margin: 0;
}

.loading-dots {
    display: flex;
    gap: 6px;
    margin-top: 4px;
}

.loading-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.7);
    animation: loading-dot-bounce 1.2s ease-in-out infinite;
}

.loading-dots span:nth-child(1) {
    animation-delay: 0s;
}

.loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes loading-dot-bounce {
    0%,
    80%,
    100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

/* Dark mode styles */
.continue-outer-container.dark-mode .welcome-title {
    color: white;
}

.continue-outer-container.dark-mode .continue-description {
    color: rgba(255, 255, 255, 0.75);
    font-weight: 300;
}

.continue-outer-container.dark-mode .link_terms {
    color: rgba(255, 255, 255, 0.75);
}

.continue-outer-container.dark-mode .shadow-btn {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    box-shadow: none;
    border-radius: 52px;
}

.continue-outer-container.dark-mode .google-button,
.continue-outer-container.dark-mode .x-button,
.continue-outer-container.dark-mode .email-button {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
}

.continue-outer-container.dark-mode .email-button {
    filter: brightness(0) invert(1);
}

.continue-outer-container.dark-mode .divider-line {
    background-color: rgba(255, 255, 255, 0.2);
}

.continue-outer-container.dark-mode .divider-text {
    color: rgba(255, 255, 255, 0.6);
}

.continue-outer-container.dark-mode .input-field {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: white !important;
}

.continue-outer-container.dark-mode .input-field::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.continue-outer-container.dark-mode input[data-pc-name='inputtext'] {
    background-color: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.75) !important;
}

.continue-outer-container.dark-mode
    input[data-pc-name='inputtext']::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.continue-outer-container.dark-mode .forgot-password-link a {
    color: rgba(255, 255, 255, 0.75);
}
</style>

<style>
/* Set input field styles */
.p-inputtext {
    width: 100%;
    box-sizing: border-box;
}

/* Password field styles */
#password input {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    background-color: #fff;
    border: 1px solid #ddd !important;
    color: #333 !important;
    z-index: 1;
}

#password input::placeholder {
    color: #999 !important;
}

#password input:focus {
    outline: 0 none;
    outline-offset: 0;
    box-shadow: none;
    border-color: #9B6E2B !important;
}

/* Dark mode password field */
.continue-outer-container.dark-mode #password input {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: white !important;
}

.continue-outer-container.dark-mode #password input::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.continue-outer-container.dark-mode #password input:focus {
    border-color: rgba(255, 255, 255, 0.4) !important;
}
</style>
